import { DrawerContact } from '..';
import {
  AddressInput,
  Contact,
  EmailAddressInput,
  LicenseInput,
  PartyContact,
  PartyContactInput,
  PhoneNumberInput,
} from '../__graphql_generated__';

const safeArray = <T>(input: T[] | undefined | null): T[] => input ?? [];
export const toContactInputsV2 = () => {
  const emailAddressesInput = (contact: Partial<Contact | PartyContact>): EmailAddressInput[] => {
    return safeArray(contact?.emailAddresses)?.map((value) => {
      return { emailAddress: value?.emailAddress, isPrimary: value?.isPrimary };
    });
  };

  const phoneNumbersInput = (contact: Partial<Contact | PartyContact>): PhoneNumberInput[] => {
    return safeArray(contact?.phoneNumbers)?.map((value) => {
      return {
        ext: value?.ext ?? undefined,
        isPrimary: value?.isPrimary ?? false,
        phoneNumber: value?.phoneNumber ?? '',
        type: value?.type ?? '',
      };
    });
  };

  const licenseInput = (contact: Partial<Contact | PartyContact>): LicenseInput[] =>
    safeArray(contact?.licenses)?.map((item) => ({
      licenseNumber: item?.licenseNumber ?? undefined,
      licensedState: item?.licensedState ?? undefined,
    }));

  const address = (contact: Partial<Contact | PartyContact>): AddressInput | undefined => {
    if (!contact?.address) {
      return undefined;
    }
    return {
      address1: contact?.address?.address1 ?? undefined,
      address2: contact?.address?.address2 ?? undefined,
      addressType: contact?.address?.addressType ?? undefined,
      city: contact?.address?.city ?? undefined,
      country: contact?.address?.country ?? undefined,
      countyFips: contact?.address?.countyFips ?? undefined,
      countyName: contact?.address?.countyName ?? undefined,
      name: contact?.address?.name ?? undefined,
      postalCode: contact?.address?.postalCode ?? undefined,
      region: contact?.address?.region ?? undefined,
      stateOrProvince: contact?.address?.stateOrProvince ?? undefined,
      usePrimaryAddress: contact?.address?.usePrimaryAddress ?? undefined,
    };
  };

  const getContact = (data: Contact | PartyContact | DrawerContact): Contact | PartyContact | null => {
    if ('value' in data) {
      return data?.value ?? null;
    }
    if ('contact' in data) {
      return data?.contact ?? null;
    }
    return data as Contact | PartyContact;
  };

  const toPartyContact = (data: Contact | PartyContact | DrawerContact): PartyContactInput | null => {
    const contact = getContact(data);

    if (!contact) return null;

    const directoryContactReferenceId = (): string | undefined => {
      if ('directoryContactReferenceId' in contact) {
        return contact.directoryContactReferenceId ?? undefined;
      }
      if ('directoryContactId' in contact) {
        return contact.directoryContactId as string;
      }
      if ('id' in contact) {
        return contact.id;
      }
      return undefined;
    };

    return {
      address: address(contact),
      altaId: contact?.altaId ?? undefined,
      directoryContactReferenceId: directoryContactReferenceId(),
      emailAddresses: emailAddressesInput(contact),
      firstName: contact?.firstName ?? undefined,
      middleName: contact?.middleName ?? undefined,
      jobTitle: contact?.jobTitle ?? undefined,
      lastName: contact?.lastName ?? undefined,
      licenses: licenseInput(contact),
      nmlsId: contact?.nmlsId ?? undefined,
      phoneNumbers: phoneNumbersInput(contact),
      suffix: contact?.suffix ?? undefined,
      title: contact?.title ?? undefined,
      useCompanyAddress: contact?.useCompanyAddress,
    };
  };

  const toEditContactInput = (partyContact: Partial<Contact | PartyContact>, directoryCompanyReferenceId?: any) => {
    return {
      address: address(partyContact),
      altaId: partyContact?.altaId ?? undefined,
      directoryCompanyId: directoryCompanyReferenceId,
      directoryContactId:
        'directoryContactReferenceId' in partyContact ? partyContact?.directoryContactReferenceId : undefined,
      emailAddresses: emailAddressesInput(partyContact),
      firstName: partyContact?.firstName ?? undefined,
      jobTitle: partyContact?.jobTitle ?? undefined,
      lastName: partyContact?.lastName ?? undefined,
      licenses: licenseInput(partyContact),
      middleName: partyContact?.middleName ?? undefined,
      nmlsId: partyContact?.nmlsId ?? undefined,
      phoneNumbers: phoneNumbersInput(partyContact),
      suffix: partyContact?.suffix ?? undefined,
      title: partyContact?.title ?? undefined,
      useCompanyAddress: partyContact?.useCompanyAddress ?? true,
    };
  };

  const toUpdateCreateDirectoryContactInput = (input: Partial<Contact | PartyContact>) => {
    return {
      address: input.useCompanyAddress ? {} : address(input),
      emailAddresses: emailAddressesInput(input),
      firstName: input?.firstName ?? undefined,
      jobTitle: input?.jobTitle ?? undefined,
      lastName: input?.lastName ?? undefined,
      licenses: licenseInput(input),
      middleName: input?.middleName ?? undefined,
      altaId: input?.altaId ?? undefined,
      nmlsId: input?.nmlsId ?? undefined,
      phoneNumbers: phoneNumbersInput(input),
      suffix: input?.suffix ?? undefined,
      title: input?.title ?? undefined,
      useCompanyAddress: input.useCompanyAddress ?? false,
    };
  };

  return { toPartyContact, toEditContactInput, toUpdateCreateDirectoryContactInput };
};
